// src/admin/components/TaskAssignmentWizard.jsx
import React, { useState, useEffect, useMemo, useRef } from 'react';
import PropTypes from 'prop-types';
import useAdminAssets from '../hooks/useAdminAssets';
import useAdminWorkflows from '../hooks/useAdminWorkflows';
import ModalSearchBar from './ModalSearchBar';
import AssetSelectionList from './AssetSelectionList';
import WorkflowSelectionList from './WorkflowSelectionList';
import FooterControls from './FooterControls';
import AssetFormFields from './assets/AssetFormFields';
import FormInput from '../../shared/components/FormInput';
import DurationFormInput from '../../shared/components/DurationFormInput';
import { fetchWithAuth } from '../auth/fetchWithAuth';
import AdminModal from './common/AdminModal';
import { useCurrentUser } from '../auth/CurrentUserContext';
import TaskAssignedModal from './TaskAssignedModal';
import TicketPicker from './TicketPicker';
import { API_URL } from '../../config/env';

const RECENT_KEY = 'recentWorkflows'; // localStorage key

export default function TaskAssignmentWizard({ 
  show, 
  onClose, 
  initialQuery = '',
  initialAssetId = null,
  initialTaskId = null,
}) {
  const { user } = useCurrentUser();
  const [step, setStep] = useState(1);
  const [minStep, setMinStep] = useState(1); // Tracks starting step for back navigation
  const [assetSearch, setAssetSearch] = useState('');
  const [workflowSearch, setWorkflowSearch] = useState('');
  const [selectedAsset, setSelectedAsset] = useState(null);
  const [selectedWorkflow, setSelectedWorkflow] = useState(null);
  const [initLoading, setInitLoading] = useState(false);
  const [initError, setInitError] = useState(null);
  const [editingTaskId, setEditingTaskId] = useState(null); // Track if editing existing task
  const [taskWasAssigned, setTaskWasAssigned] = useState(false); // Track if task was already assigned

  const { data: assets, error: assetError, refresh: refreshAssets } = useAdminAssets();
  // Scoped to the chosen asset so step 2 can only offer workflows whose tag scope covers it —
  // the same boundary Tasks::AssignableWorkflowResolver enforces on submit. Before an asset is
  // chosen there is nothing to scope by, so the unfiltered list loads.
  const { data: workflows, error: workflowError, refresh: refreshWorkflows } = useAdminWorkflows(
    selectedAsset ? `asset_id=${selectedAsset.id}` : ''
  );
  const [assetForm, setAssetForm] = useState({
    asset_name: '',
    asset_make: '',
    asset_model: '',
    asset_ext_id: '',
    street_address: '',
    city: '',
  });
  const [taskForm, setTaskForm] = useState({
    assigned_to_email: '',
    ticket_reference: '',
    instructions: '',
    access_duration: '30d',
  });
  const [taskResult, setTaskResult] = useState(null);
  const [taskError, setTaskError] = useState(null);
  // The ticket chosen from the upstream ticketing system, when one is configured.
  const [selectedTicket, setSelectedTicket] = useState(null);
  const assetSearchInputRef = useRef(null);

  // when user selects an asset in step 1:
  useEffect(() => {
    if (selectedAsset) {
      setAssetForm({
        asset_name: selectedAsset.asset_name || '',
        asset_make: selectedAsset.asset_make || '',
        asset_model: selectedAsset.asset_model || '',
        asset_ext_id: selectedAsset.asset_ext_id || '',
        street_address: selectedAsset.street_address || '',
        city: selectedAsset.city || '',
      });
    }
  }, [selectedAsset]);

  // Clear assetSearch and workflowSearch when entering step 2
  useEffect(() => {
    if (step === 2) {
      setAssetSearch('');
      setWorkflowSearch('');
    }
  }, [step]);

  // handle final submission
  const handleSubmit = async () => {
    try {
      let res;
      
      if (editingTaskId) {
        // Update existing task
        const updatePayload = {
          task: {
            assigned_to_email: taskForm.assigned_to_email,
            instructions: taskForm.instructions,
            ticket_reference: taskForm.ticket_reference,
            // Always sent on update so clearing the selection clears the link; the backend
            // only touches the link when this key is present.
            ticket_external_id: selectedTicket?.external_id || '',
          },
          access_duration: taskForm.access_duration,
        };
        
        res = await fetchWithAuth(
          `${API_URL}/v1/admin/tasks/${editingTaskId}`,
          {
            method: 'PATCH',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(updatePayload),
          }
        );
      } else {
        // Create new task
        const createPayload = {
          task: {
            assigned_by_email: user?.email || '',
            assigned_to_email: taskForm.assigned_to_email,
            instructions: taskForm.instructions,
            ticket_reference: taskForm.ticket_reference,
            ...(selectedTicket && { ticket_external_id: selectedTicket.external_id }),
            workflow_id: selectedWorkflow.id,
          },
          asset: {
            ...(selectedAsset.id && { id: selectedAsset.id }),
            asset_ext_id: assetForm.asset_ext_id,
            asset_name: assetForm.asset_name,
            asset_make: assetForm.asset_make,
            asset_model: assetForm.asset_model,
            asset_type: selectedAsset.asset_ext_id ? 'external' : 'internal',
            street_address: assetForm.street_address,
            city: assetForm.city,
          },
          access_duration: taskForm.access_duration,
        };
        
        res = await fetchWithAuth(
          `${API_URL}/v1/admin/tasks/create_assign`,
          {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(createPayload),
          }
        );
      }
      
      if (!res.ok) throw new Error(res.statusText);
      
      const result = await res.json();
      // The API returns the task directly, not nested under 'task'
      setTaskResult({
        asset: result.asset || selectedAsset,
        workflow: result.workflow || selectedWorkflow,
        task: result
      });
      setTaskError(null);
      setStep(4);
    } catch (err) {
      console.error(err);
      setTaskError(err.message || (editingTaskId ? 'Failed to update task' : 'Failed to create task'));
      setTaskResult(null);
      setStep(4);
    }
  };

  // Recently used workflow IDs
  const [recentIds, setRecentIds] = useState([]);

  // The fetchers are held in refs because the open-reset effect below calls them but must not
  // re-run when their identity changes: the workflow fetcher is rebound whenever the scoped
  // asset changes, and re-running the reset would clear the very selection that scoped it.
  const refreshAssetsRef = useRef(refreshAssets);
  const refreshWorkflowsRef = useRef(refreshWorkflows);
  useEffect(() => {
    refreshAssetsRef.current = refreshAssets;
    refreshWorkflowsRef.current = refreshWorkflows;
  }, [refreshAssets, refreshWorkflows]);

  // Reset on open and handle initial asset/task
  useEffect(() => {
    if (show) {
      // Reset common state
      setAssetSearch(initialQuery || '');
      setWorkflowSearch('');
      setSelectedAsset(null);
      setSelectedWorkflow(null);
      setTaskResult(null);
      setTaskError(null);
      setInitError(null);
      setEditingTaskId(null);
      setTaskWasAssigned(false);
      setSelectedTicket(null);
      setTaskForm({
        assigned_to_email: '',
        ticket_reference: '',
        instructions: '',
        access_duration: '30d',
      });
      setRecentIds(JSON.parse(localStorage.getItem(RECENT_KEY) || '[]'));
      refreshAssetsRef.current();
      refreshWorkflowsRef.current();

      // Determine starting step based on initial props
      if (initialTaskId) {
        // Fetch task and start at step 3
        setInitLoading(true);
        setStep(3);
        setMinStep(3);
        setEditingTaskId(initialTaskId);
        fetchWithAuth(`${API_URL}/v1/admin/tasks/${initialTaskId}`)
          .then(res => {
            if (!res.ok) throw new Error('Failed to load task');
            return res.json();
          })
          .then(task => {
            // Workflow comes from task response
            setSelectedWorkflow(task.workflow);
            // Track if task was already assigned
            setTaskWasAssigned(!!task.assigned_to_email);
            // Populate task form with existing values
            setTaskForm({
              assigned_to_email: task.assigned_to_email || '',
              ticket_reference: task.ticket_reference || '',
              instructions: task.instructions || '',
              access_duration: '30d', // Fresh duration for new assignment
            });
            setSelectedTicket(task.ticket || null);
            // Asset needs to be looked up from assets list (handled in separate effect)
            setAssetSearch(`__asset_id:${task.asset_id}`);
            setInitLoading(false);
          })
          .catch(err => {
            console.error('Error loading task:', err);
            setInitError('Failed to load task');
            setInitLoading(false);
          });
      } else if (initialAssetId) {
        // Start at step 2, asset lookup handled in separate effect
        setStep(2);
        setMinStep(2);
        setAssetSearch(`__asset_id:${initialAssetId}`);
      } else {
        // Normal flow - start at step 1
        setStep(1);
        setMinStep(1);
        requestAnimationFrame(() => {
          const input = assetSearchInputRef.current;
          if (!input) return;
          input.focus();
          if (initialQuery) input.select();
        });
      }
    }
  }, [show, initialQuery, initialAssetId, initialTaskId]);

  // Lookup asset by ID once assets are loaded (for initialAssetId or initialTaskId cases)
  useEffect(() => {
    if (show && assetSearch.startsWith('__asset_id:') && assets.length > 0) {
      const assetId = parseInt(assetSearch.replace('__asset_id:', ''), 10);
      const asset = assets.find(a => a.id === assetId);
      if (asset) {
        setSelectedAsset(asset);
        setAssetSearch('');
      } else {
        // Asset not in list - could be edge case, fetch directly
        fetchWithAuth(`${API_URL}/v1/admin/assets/${assetId}`)
          .then(res => {
            if (!res.ok) throw new Error('Asset not found');
            return res.json();
          })
          .then(asset => {
            setSelectedAsset(asset);
            setAssetSearch('');
          })
          .catch(err => {
            console.error('Error loading asset:', err);
            setInitError('Failed to load asset');
            setAssetSearch('');
          });
      }
    }
  }, [show, assetSearch, assets]);

  // client-side filter for assets
  const filteredAssets = useMemo(() => {
    const q = String(assetSearch || '').toLowerCase();
    if (!q) return assets;
    return assets.filter((a) =>
      [a.asset_name, a.asset_make, a.asset_model, a.asset_ext_id, a.street_address, a.city]
        .some((field) => String(field || '').toLowerCase().includes(q))
    );
  }, [assets, assetSearch]);

  const publishedWorkflows = useMemo(
    () =>
      (workflows || []).filter(
        (w) => w.state === 'published' && !w.user_workflow
      ),
    [workflows]
  );

  // Assignable workflows: published maintenance/self-startable only (not user/public flows)
  const filteredWorkflows = useMemo(() => {
    const q = String(workflowSearch || '').toLowerCase();
    return publishedWorkflows.filter((w) =>
      [w.name, w.description].some((field) =>
        String(field || '')
          .toLowerCase()
          .includes(q)
      )
    );
  }, [publishedWorkflows, workflowSearch]);

  const saveRecentWorkflow = wf => {
    const next = [wf.id, ...recentIds.filter(id => id !== wf.id)].slice(0, 5);
    setRecentIds(next);
    localStorage.setItem(RECENT_KEY, JSON.stringify(next));
  };

  if (!show) return null;

  // Loading state for initial task/asset fetch
  if (initLoading) {
    return (
      <AdminModal show={show} onClose={onClose} title="Loading...">
        <div className="text-center py-4">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
        </div>
      </AdminModal>
    );
  }

  // Error state for initialization
  if (initError) {
    return (
      <AdminModal show={show} onClose={onClose} title="Error">
        <div className="alert alert-danger">{initError}</div>
        <div className="d-flex justify-content-end">
          <button className="btn btn-secondary" onClick={onClose}>Close</button>
        </div>
      </AdminModal>
    );
  }

  // Step 4 (Task Assigned Modal)
  if (step === 4) {
    return (
      <TaskAssignedModal
        show={show}
        onClose={onClose}
        taskData={taskResult}
        error={taskError}
      />
    );
  }

  // Step 1 (Asset)
  if (step === 1) {
    const body = (
      <>
        <ModalSearchBar
          value={assetSearch}
          onChange={setAssetSearch}
          placeholder="Search assets..."
          inputRef={assetSearchInputRef}
        />
        {assetError && <p className="text-danger">Error loading assets</p>}
        <AssetSelectionList
          assets={filteredAssets}
          onSelect={(selection) => {
            setSelectedAsset(selection);
            setStep(2);
          }}
        />
      </>
    );
    const footer = (
      <FooterControls
        step={1}
        totalSteps={3}
        onCancel={onClose}
        onBack={() => {}}
        onNext={() => setStep(2)}
        nextHint={'Select asset to continue'}
        disableNext={!selectedAsset}
      />
    );
    return (
      <AdminModal show={show} onClose={onClose} title="Select Asset" footer={footer}>
      {body}
    </AdminModal>
    );
  }

  // Step 2 (Workflow)
  if (step === 2) {
    const body = (
      <>
        <ModalSearchBar value={workflowSearch} onChange={setWorkflowSearch} placeholder="Search workflows..." />
        {workflowError && <p className="text-danger">Error loading workflows</p>}
        <WorkflowSelectionList
          workflows={filteredWorkflows}
          recentIds={recentIds}
          onSelect={wf => {
            setSelectedWorkflow(wf);
            saveRecentWorkflow(wf);
            setStep(3);
          }}
        />
      </>
    );
    const footer = (
      <FooterControls
        step={2}
        totalSteps={3}
        onCancel={onClose}
        onBack={minStep >= 2 ? onClose : () => setStep(1)}
        onNext={() => setStep(3)}
        nextHint={'Select a workflow to continue'}
        disableNext={!selectedWorkflow}
        backLabel={minStep >= 2 ? 'Cancel' : 'Back'}
      />
    );
    return (
      <AdminModal show={show} onClose={onClose} title="Select Workflow" footer={footer}>
        {body}
      </AdminModal>
    );
  }

  // Step 3 (Task Details)
  if (step === 3) {
    const body = (
      <div>
        {/* ASSET INFORMATION - Top section with 2-column grid */}
        <div className="mb-4">
          <h6 className="text-uppercase text-muted small mb-3">Asset Information</h6>
          <AssetFormFields
            assetForm={assetForm}
            onChange={setAssetForm}
            mode="edit"
            compact
            readOnlyExtId
          />
        </div>

        {/* TASK DETAILS - Bottom section with full-width inputs */}
        <div>
          <h6 className="text-uppercase text-muted small mb-3">Task Details</h6>
          <FormInput
            id="assigned_to_email"
            type="email"
            label={(
              <>
                Assigned To
                <span className="text-muted ms-1" style={{ fontSize: '0.75rem' }}>(optional)</span>
              </>
            )}
            placeholder="Enter email address"
            value={taskForm.assigned_to_email}
            onChange={e => setTaskForm(f => ({ ...f, assigned_to_email: e.target.value }))}
            helperText="Leave empty to create an unassigned task."
          />
          <TicketPicker
            selected={selectedTicket}
            reference={taskForm.ticket_reference}
            onSelect={setSelectedTicket}
            onReferenceChange={val => setTaskForm(f => ({ ...f, ticket_reference: val }))}
          />
          <FormInput
            id="instructions"
            label="Instructions"
            rows={4}
            placeholder="Enter task instructions"
            value={taskForm.instructions}
            onChange={e => setTaskForm(f => ({ ...f, instructions: e.target.value }))}
          />
          <DurationFormInput
            id="access_duration"
            label="Access Duration"
            value={taskForm.access_duration}
            onChange={val => setTaskForm(f => ({ ...f, access_duration: val }))}
            mode="string"
            placeholder="e.g., 30d, 2w, 3mo"
          />
        </div>
      </div>
    );
    const hasAssignee = taskForm.assigned_to_email.trim() !== '';
    
    // Determine button label based on context
    let nextLabel;
    if (editingTaskId) {
      // Editing existing task
      if (taskWasAssigned) {
        nextLabel = 'Update Task';
      } else {
        nextLabel = hasAssignee ? 'Assign Task' : 'Update Task';
      }
    } else {
      // Creating new task
      nextLabel = hasAssignee ? 'Create & Assign' : 'Create Task';
    }
    
    const footer = (
      <FooterControls
        step={3}
        totalSteps={3}
        onCancel={onClose}
        onBack={minStep >= 3 ? onClose : () => setStep(2)}
        onNext={handleSubmit}
        nextLabel={nextLabel}
        backLabel={minStep >= 3 ? 'Cancel' : 'Back'}
      />
    );
    return (
      <AdminModal show={show} onClose={onClose} title="Task Details" footer={footer}>
        {body}
      </AdminModal>
    );
  }
}

TaskAssignmentWizard.propTypes = {
  show: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  initialQuery: PropTypes.string,
  initialAssetId: PropTypes.number,
  initialTaskId: PropTypes.number,
};
